import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  keyframes,
  List,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { memo, useContext, useMemo, useRef, useState } from "react";
import DrawerHeader from "./DrawerHeader";
import { ChevronRight, Send } from "@mui/icons-material";
import useAutoScroll from "hooks/useAutoScroll";
import ChatBubble from "./ChatBubble";
import { LoadingButton } from "@mui/lab";
import { AiMessage } from "hooks/useLLM";
import useResize from "hooks/useResize";
import { UtilityContext } from "contexts";
import MarkdownViewer from "./MarkdownViewer";
import { HEADER_HEIGHT } from "common/theme";
import { getTranslation } from "common";
import { useTranslations, useUser } from "hooks";
import { Button } from "@mui/base";
import PromptSuggest from "./PromptSuggest";
import promptSuggestions from "./promptSuggestion.json"

function pauseEvent(e: any) {
  if (e.stopPropagation) e.stopPropagation();
  if (e.preventDefault) e.preventDefault();
  e.cancelBubble = true;
  e.returnValue = false;
  return false;
}
interface AiDrawerProps {
  variant?: "persistent" | "permanent" | "temporary";
  aiAnswers: AiMessage[];
  handleSubmitLLM: () => void;
  openAI: boolean;
  setOpenAI: (value: React.SetStateAction<boolean>) => void;
  LLMLoading: boolean;
  handleApplyAi?: (model: string) => Promise<void>;
  llmInputRef: React.RefObject<HTMLInputElement>;
  modelInputRef: React.RefObject<HTMLInputElement>;
  canUseAi?: boolean;
  isDashboard?: boolean;
}

const AiDrawer: React.FC<AiDrawerProps> = ({
  aiAnswers,
  handleSubmitLLM,
  openAI,
  setOpenAI,
  handleApplyAi,
  LLMLoading,
  llmInputRef,
  modelInputRef,
  variant,
  canUseAi = true,
  isDashboard
}) => {
  const translations = useTranslations();
  const {
    state: { modelChoice },
  } = useContext(UtilityContext);
  const questions = useMemo(
    () => aiAnswers.filter(({ isAi }) => !isAi),
    [aiAnswers]
  );
  const questionSelectedIdxRef = useRef(0);
  const { enableResize, width } = useResize({
    minWidth: 100,
    defaultWidth: 500,
    side: "right",
    maxWidth: (window.innerWidth * 60) / 100,
  });

  const listRef = useAutoScroll(true, []);
  const user = useUser();
  const lang = user?.i18n || navigator.language.split("-")[0];
  const [isCardView, setIsCardView] = useState(true);

  const glow = keyframes`
  0% {
    opacity: 0.6;
    transform: translateY(0px) scale(1);
    filter: brightness(1);
  }
  50% {
    opacity: 1;
    transform: translateY(-2px) scale(1.02);
    filter: brightness(1.2);
  }
  100% {
    opacity: 0.6;
    transform: translateY(0px) scale(1);
    filter: brightness(1);
  }
`;

  const dots = keyframes`
  0%, 20% {
    content: '.';
  }
  40% {
    content: '..';
  }
  60% {
    content: '...';
  }
  80%, 100% {
    content: '';
  }
`;



  const handlePromptSelect = (prompt: string) => {
    if (llmInputRef.current) {
      llmInputRef.current.value = prompt;
      // handleSubmitLLM()
      setIsCardView(false);
    }
  };

  return (
    <Drawer
      sx={{
        width,
        flexShrink: 0,
        zIndex: 9999999,
        overflowX: "hidden",
        "& .MuiDrawer-paper": {
          mt: HEADER_HEIGHT,
          height: `calc(100% - ${HEADER_HEIGHT})`,
          width,
          boxSizing: "border-box",
        },
      }}
      variant={variant}
      anchor="right"
      open={openAI}
    >
      <div
        id="dragger"
        onMouseDown={(e) => {
          e = e || window.event;
          pauseEvent(e);
          enableResize(e);
        }}
        style={{
          width: "5px",
          cursor: "ew-resize",
          padding: "4px 0 0",
          borderTop: "1px solid #ddd",
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: "100",
          backgroundColor: "#f4f7f9",
        }}
      />
      <DrawerHeader sx={{ justifyContent: "start" }}>
        <IconButton onClick={() => setOpenAI(false)}>
          <ChevronRight />
        </IconButton>
        {modelChoice && (
          <TextField
            size="small"
            fullWidth
            label=" AI model"
            inputRef={modelInputRef}
          />
        )}
      </DrawerHeader>
      <Divider />
      <List
        ref={listRef}
        sx={{
          overflowX: "hidden",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          py: 2,
          px: 4,
          overflow: "auto",
          flexShrink: 0,
        }}
      >
        {aiAnswers.map(
          ({ uuid, isAi, message, isLoading, hidden, parsing }, i) =>
            !hidden && (
              <React.Fragment key={uuid}>
                {isLoading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 2,
                      py: 1,
                      animation: `${glow} 2s ease-in-out infinite`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'black',
                        fontWeight: 500,
                        letterSpacing: 0.5,
                      }}
                    >
                      {promptSuggestions[lang.toUpperCase() as unknown as keyof typeof promptSuggestions].response_load}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        color: 'white',
                        fontWeight: 500,
                        '&::after': {
                          content: '""',
                          animation: `${dots} 1.5s infinite`,
                        }
                      }}
                    />
                  </Box>
                ) : (
                  <ChatBubble isAi={isAi}>
                    <MarkdownViewer
                      handleApplyAi={handleApplyAi}
                      message={message}
                      uuid={uuid}
                    />
                    {/* {void console.log("render", i)} */}
                    {Boolean(parsing) && (
                      <Skeleton
                        animation="pulse"
                        width={"100%"}
                        sx={{
                          maxWidth: width - 100,
                        }}
                        height={160}
                      />
                    )}
                  </ChatBubble>
                )}
              </React.Fragment>
            )
        )}
      </List>
      {isCardView &&
        <PromptSuggest
          onPromptSelect={handlePromptSelect}
          suggestions={
            isDashboard
              ? promptSuggestions[lang.toUpperCase() as unknown as keyof typeof promptSuggestions].dashboardSuggestions
              : promptSuggestions[lang.toUpperCase() as unknown as keyof typeof promptSuggestions].suggestions
          }
        />
      }

      <Box sx={{ p: 2, mb: 10 }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmitLLM();
            setIsCardView(false);
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-end">
            <TextField
              inputRef={llmInputRef}
              size="small"
              multiline
              maxRows={10}
              minRows={1}
              InputProps={{
                sx: {
                  fontSize: '14px',
                  lineHeight: '1.5',
                  py: 1,
                  px: 1.5,
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  maxHeight: '200px', // Altezza massima prima dello scroll
                  overflowY: 'auto',
                },
              }}
              onKeyDown={(e) => {
                // Gestione Enter per inviare (Shift+Enter per andare a capo)
                if (e.key === 'Enter') {
                  if (!e.shiftKey && !e.ctrlKey) {
                    e.preventDefault();
                    handleSubmitLLM();
                    setIsCardView(false);
                  }
                }

                // Il tuo codice esistente per navigare nella cronologia con le frecce
                if (["ArrowUp", "ArrowDown"].includes(e.code)) {
                  if (e.code === "ArrowUp") {
                    questionSelectedIdxRef.current =
                      questionSelectedIdxRef.current + 1 > questions.length
                        ? questions.length
                        : questionSelectedIdxRef.current + 1;
                  } else if (e.code === "ArrowDown") {
                    questionSelectedIdxRef.current =
                      questionSelectedIdxRef.current - 1 < 0
                        ? 0
                        : questionSelectedIdxRef.current - 1;
                  }
                  if (llmInputRef.current) {
                    if (questionSelectedIdxRef.current === 0) {
                      llmInputRef.current.value = "";
                    } else {
                      llmInputRef.current.value =
                        questions[
                          questions.length - questionSelectedIdxRef.current
                        ].message;
                    }
                  }
                }
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  paddingTop: '8px',
                  paddingBottom: '8px',
                },
                '& .MuiInputBase-input': {
                  scrollbarWidth: 'thin', // Per Firefox
                  '&::-webkit-scrollbar': { // Per Chrome/Safari
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#888',
                    borderRadius: '10px',
                    '&:hover': {
                      background: '#555',
                    },
                  },
                },
              }}
              // onChange={(e) => setUserPrompt(e.target.value)}
              // value={userPrompt}
              placeholder={getTranslation(translations, "ask_ai_placeholder")}
            />
            <LoadingButton loading={false} type="submit" disabled={!canUseAi}>
              <Send />
            </LoadingButton>
          </Stack>
        </form>
      </Box>
    </Drawer>
  );
};
// mi sono appena creato un'organizzazione, come vado avanti?
export default memo(AiDrawer, (prev, next) => prev.variant === next.variant &&
  prev.aiAnswers === next.aiAnswers &&
  prev.openAI === next.openAI &&
  prev.LLMLoading === next.LLMLoading &&
  prev.canUseAi === next.canUseAi
);
